import { useResponsive } from '@/shared/hooks/useResponsive.js';
import { Loader2 } from 'lucide-react';

const RouteFallback = () => {
  const { isDesktop } = useResponsive();

  if (isDesktop) {
    return (
      <div className="fixed inset-0 z-[100] h-screen w-screen overflow-hidden bg-slate-50 transition-colors duration-300 dark:bg-gray-900">
        {/* Desktop Sidebar Skeleton */}
        <aside className="border-tertiary-10 fixed top-0 right-0 z-50 h-full w-72 border-l bg-white/95 backdrop-blur-xl dark:border-gray-700/50 dark:bg-gray-800/95">
          <div className="border-tertiary-10 flex h-16 items-center justify-center border-b px-4 dark:border-gray-700/50">
            <div className="bg-tertiary-10 h-10 w-36 animate-pulse rounded-lg dark:bg-gray-700" />
          </div>
          <div className="mt-6 space-y-2 p-4">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="bg-tertiary-10 flex h-12 w-full animate-pulse items-center gap-3 rounded-xl px-4 dark:bg-gray-700/50"
              >
                <div className="bg-tertiary-20 h-5 w-5 rounded dark:bg-gray-600" />
                <div className="bg-tertiary-20 h-4 flex-1 rounded dark:bg-gray-600" />
              </div>
            ))}
          </div>
        </aside>

        {/* Desktop Header Skeleton - Updated to match the real components */}
        <header className="border-tertiary-10 fixed top-0 right-72 left-0 z-40 flex h-16 items-center justify-between border-b bg-white/95 px-6 shadow-lg backdrop-blur-xl dark:border-gray-800/50 dark:bg-gray-900/95 dark:shadow-none">
          {/* Tenant Select Skeleton */}
          <div className="w-64">
            <div className="bg-tertiary-10 h-9 w-full animate-pulse rounded-md dark:bg-gray-700" />
          </div>

          {/* Right side Icons Skeleton */}
          <div className="flex items-center gap-4">
            {/* Theme Toggle Skeleton */}
            <div className="bg-tertiary-10 h-10 w-10 animate-pulse rounded-full dark:bg-gray-700" />

            {/* Profile Dropdown Skeleton */}
            <div className="flex items-center gap-2">
              <div className="bg-tertiary-10 h-10 w-10 animate-pulse rounded-xl dark:bg-gray-700" />
              <div className="hidden flex-col gap-1.5 lg:flex">
                <div className="bg-tertiary-10 h-3 w-16 animate-pulse rounded dark:bg-gray-700" />
                <div className="bg-tertiary-20 h-2 w-10 animate-pulse rounded dark:bg-gray-600" />
              </div>
            </div>
          </div>
        </header>

        {/* Desktop Main Content */}
        <main className="absolute top-16 right-72 bottom-0 left-0 flex items-center justify-center overflow-hidden">
          <div className="flex flex-col items-center gap-4">
            <Loader2
              className="text-primary-100 h-13 w-13 animate-spin dark:text-orange-500"
              strokeWidth={2.5}
            />
          </div>
        </main>
      </div>
    );
  }

  // Mobile Layout
  return (
    <div className="fixed inset-0 z-[100] h-screen w-screen overflow-hidden bg-slate-50 transition-colors duration-300 dark:bg-gray-900">
      {/* Mobile Header Skeleton */}
      <header className="shadow-tertiary-6 fixed top-0 right-0 left-0 z-50 flex h-16 items-center justify-between bg-white px-4 backdrop-blur-xl dark:bg-gray-800 dark:shadow-none">
        <div className="bg-tertiary-10 h-10 w-10 animate-pulse rounded-xl dark:bg-gray-700" />
        <div className="bg-tertiary-10 h-8 w-32 animate-pulse rounded-lg dark:bg-gray-700" />
        <div className="bg-tertiary-10 h-10 w-10 animate-pulse rounded-full dark:bg-gray-700" />
      </header>

      {/* Mobile Main Content */}
      <main
        className="absolute right-0 left-0 flex items-center justify-center overflow-hidden"
        style={{
          top: 'calc(4rem + env(safe-area-inset-top, 0px))',
          bottom: 'calc(4rem + env(safe-area-inset-bottom, 0px))',
        }}
      >
        <div className="flex flex-col items-center gap-3">
          <Loader2
            className="text-primary-100 h-10 w-10 animate-spin dark:text-orange-500"
            strokeWidth={2.5}
          />
        </div>
      </main>

      {/* Mobile Bottom Nav Skeleton */}
      <div className="fixed right-0 bottom-0 left-0 z-50">
        <nav className="border-tertiary-10 border-t bg-white shadow-lg backdrop-blur-xl dark:border-gray-800/50 dark:bg-gray-900 dark:shadow-2xl">
          <div className="flex items-center justify-around px-6">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="relative flex flex-1 items-center justify-center py-2"
              >
                <div className="bg-tertiary-10 h-10 w-10 animate-pulse rounded-xl dark:bg-gray-700" />
              </div>
            ))}
          </div>

          <div
            style={{
              height: 'env(safe-area-inset-bottom, 0px)',
            }}
            className="w-full"
          />
        </nav>
      </div>
    </div>
  );
};

export default RouteFallback;
